/**
 * Exponential backoff for live polling when the data feed stalls.
 *
 * A live page that re-asks for data on a fixed cadence has no notion of "the feed is dead", so when a
 * source stops — a simulation ended mid-session, a boat going quiet — it keeps issuing HTTP waves
 * forever against data that is never going to change. In RaceSight this gates the live timeseries
 * page's Parquet backfill retries: the WebSocket tail costs nothing while idle, but the backfill is a
 * DuckDB read per boat and must not be retried at full rate against a dead feed.
 *
 * The model is deliberately simple: observe the data edge, and back off whenever it fails to advance.
 * Recovery is driven by whatever is still observing cheaply (the columnar stream's own frames), so a
 * returning feed is noticed even while the expensive path sits at its maximum delay.
 *
 * Pure module — no Solid, no DOM, no network.
 */

export interface StallBackoffOptions {
  /** Delay while the feed is healthy. */
  baseMs: number;
  /** Ceiling the delay is clamped to when stalled. */
  maxMs: number;
  /** Multiplier applied per consecutive stalled observation. Default 2. */
  factor?: number;
}

export interface StallBackoff {
  /**
   * Record the currently observed data-edge timestamp.
   * @returns true if the edge ADVANCED (backoff resets to baseMs), false if it stalled.
   */
  observeEdge(latestTsMs: number | null | undefined): boolean;
  /** Suggested delay before the next poll attempt. */
  delayMs(): number;
  /** Consecutive stalled observations; 0 when healthy. */
  stallCount(): number;
  /** Forget all state (source teardown / mode change). */
  reset(): void;
}

export function createStallBackoff(opts: StallBackoffOptions): StallBackoff {
  const baseMs = Math.max(1, opts.baseMs);
  const maxMs = Math.max(baseMs, opts.maxMs);
  const factor = opts.factor && opts.factor > 1 ? opts.factor : 2;

  let lastEdgeMs: number | null = null;
  let stalls = 0;

  return {
    observeEdge(latestTsMs) {
      const ts = typeof latestTsMs === "number" && Number.isFinite(latestTsMs) ? latestTsMs : null;
      // No edge at all counts as stalled: "no data yet" is not "recovered". An edge that went
      // BACKWARDS is also stalled, not advanced — a source restart replaying older timestamps is
      // exactly the case we must not treat as healthy progress.
      const advanced = ts != null && (lastEdgeMs == null || ts > lastEdgeMs);
      if (ts != null && (lastEdgeMs == null || ts !== lastEdgeMs)) {
        lastEdgeMs = ts;
      }
      if (advanced) {
        stalls = 0;
        return true;
      }
      stalls++;
      return false;
    },

    delayMs() {
      if (stalls === 0) {
        return baseMs;
      }
      const scaled = baseMs * Math.pow(factor, stalls);
      return Math.min(maxMs, scaled);
    },

    stallCount() {
      return stalls;
    },

    reset() {
      lastEdgeMs = null;
      stalls = 0;
    },
  };
}

/** Base cadence for live retry loops while the feed is healthy. */
export const LIVE_POLL_BASE_MS = 2000;
/** Ceiling once the feed is confirmed dead — still slow-polling, just not hammering. */
export const LIVE_POLL_MAX_MS = 30000;
